import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';
import PWAInstallBanner from './components/shared/PWAInstallBanner';

// Layouts
import DashboardLayout from './components/dashboard/DashboardLayout';
import MobileLayout from './components/mobile/MobileLayout';
import ProtectedRoute from './components/shared/ProtectedRoute';

// Mobile pages
import LoginCliente from './pages/mobile/LoginCliente';
import CadastroCliente from './pages/mobile/CadastroCliente';
import RedefinirSenha from './pages/mobile/RedefinirSenha';
import RecuperarSenhaMobile from './pages/mobile/RecuperarSenha';
import HomeCliente from './pages/mobile/HomeCliente';
import PedidoNovo from './pages/mobile/PedidoNovo';
import PedidoHistorico from './pages/mobile/PedidoHistorico';
import PedidoDetalheMobile from './pages/mobile/PedidoDetalhe';
import PedidoConfirmado from './pages/mobile/PedidoConfirmado';
import PerfilCliente from './pages/mobile/PerfilCliente';

// Dashboard pages
import LoginDashboard from './pages/dashboard/Login';
import RecuperarSenhaDash from './pages/dashboard/RecuperarSenha';
import DashboardHome from './pages/dashboard/Dashboard';
import PedidosLista from './pages/dashboard/pedidos/PedidosLista';
import PedidoDetalheDash from './pages/dashboard/pedidos/PedidoDetalhe';
import PedidoFormDashboard from './pages/dashboard/pedidos/PedidoFormDashboard';
import ClientesLista from './pages/dashboard/clientes/ClientesLista';
import ClienteForm from './pages/dashboard/clientes/ClienteForm';
import ProdutosLista from './pages/dashboard/produtos/ProdutosLista';
import ProdutoForm from './pages/dashboard/produtos/ProdutoForm';
import ImportarProdutos from './pages/dashboard/produtos/ImportarProdutos';
import CategoriasPage from './pages/dashboard/produtos/CategoriasPage';
import RotasLista from './pages/dashboard/rotas/RotasLista';
import RotaForm from './pages/dashboard/rotas/RotaForm';
import TransportadorasLista from './pages/dashboard/transportadoras/TransportadorasLista';
import TransportadoraForm from './pages/dashboard/transportadoras/TransportadoraForm';
import RelatorioProducao from './pages/dashboard/relatorios/RelatorioProducao';
import RelatorioRomaneio from './pages/dashboard/relatorios/RelatorioRomaneio';
import RelatorioGestao from './pages/dashboard/relatorios/RelatorioGestao';
import EmpresaForm from './pages/dashboard/empresa/EmpresaForm';
import TiposCliente from './pages/dashboard/configuracoes/TiposCliente';
import Usuarios from './pages/dashboard/configuracoes/Usuarios';
import FormasEntrega from './pages/dashboard/configuracoes/FormasEntrega';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'cliente') return <Navigate to="/home" replace />;
  return <Navigate to="/admin/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Mobile – Public */}
      <Route path="/login" element={<LoginCliente />} />
      <Route path="/cadastro" element={<CadastroCliente />} />
      <Route path="/recuperar-senha" element={<RecuperarSenhaMobile />} />
      <Route path="/redefinir-senha/:token" element={<RedefinirSenha />} />

      {/* Mobile – Protected (cliente) */}
      <Route path="/" element={<ProtectedRoute roles={['cliente']}><MobileLayout /></ProtectedRoute>}>
        <Route path="home" element={<HomeCliente />} />
        <Route path="pedidos" element={<PedidoNovo />} />
        <Route path="pedidos/ok/:id" element={<PedidoConfirmado />} />
        <Route path="pedidos/historico" element={<PedidoHistorico />} />
        <Route path="pedidos/:id" element={<PedidoDetalheMobile />} />
        <Route path="perfil" element={<PerfilCliente />} />
      </Route>

      {/* Dashboard – Public */}
      <Route path="/admin/login" element={<LoginDashboard />} />
      <Route path="/admin/recuperar-senha" element={<RecuperarSenhaDash />} />

      {/* Dashboard – Protected (admin / operador) */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin', 'operador']}><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardHome />} />
        <Route path="pedidos" element={<PedidosLista />} />
        <Route path="pedidos/novo" element={<PedidoFormDashboard />} />
        <Route path="pedidos/:id" element={<PedidoDetalheDash />} />
        <Route path="clientes" element={<ClientesLista />} />
        <Route path="clientes/novo" element={<ClienteForm />} />
        <Route path="clientes/:id" element={<ClienteForm />} />
        <Route path="produtos" element={<ProdutosLista />} />
        <Route path="produtos/importar" element={<ImportarProdutos />} />
        <Route path="produtos/novo" element={<ProdutoForm />} />
        <Route path="produtos/:id" element={<ProdutoForm />} />
        <Route path="categorias" element={<CategoriasPage />} />
        <Route path="rotas" element={<RotasLista />} />
        <Route path="rotas/nova" element={<RotaForm />} />
        <Route path="rotas/:id" element={<RotaForm />} />
        <Route path="transportadoras" element={<TransportadorasLista />} />
        <Route path="transportadoras/nova" element={<TransportadoraForm />} />
        <Route path="transportadoras/:id" element={<TransportadoraForm />} />
        <Route path="relatorios/producao" element={<RelatorioProducao />} />
        <Route path="relatorios/romaneio" element={<RelatorioRomaneio />} />
        <Route path="relatorios/gestao" element={<RelatorioGestao />} />
        <Route path="empresa" element={<EmpresaForm />} />
        <Route path="configuracoes/tipos-cliente" element={<TiposCliente />} />
        <Route path="configuracoes/usuarios" element={<Usuarios />} />
        <Route path="configuracoes/formas-entrega" element={<FormasEntrega />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <PWAInstallBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
